import { AxiosError } from 'axios';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { deeplinksApi } from 'api';
import {
  CrudTable,
  CrudTableActionType,
  DataGridColumnDefinition,
  dataGridColumns,
} from 'components';
import { useMutation, useUserContextUtils } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Deeplink } from 'types';
import { deviceUtils } from 'utils';

import { useDeeplinks } from './DeeplinksList.context';

type Props = {
  onEdit?: (item: Deeplink) => void;
};

export const DeeplinksList: React.FC<Props> = ({ onEdit }) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'features.deeplinks',
  });
  const { deeplinksQuery } = useDeeplinks();
  const { getBankName } = useUserContextUtils();

  const removeDeeplinkMutation = useMutation<Deeplink, AxiosError, string>(
    deeplinksApi.remove,
    {
      onSuccess: () => {
        deeplinksQuery.refetch();
      },
      notifierType: 'remove',
    },
  );

  const columns: DataGridColumnDefinition<Deeplink>[] = useMemo(
    () => [
      dataGridColumns.getIdColumn(),
      {
        header: t('fields.customer_bank'),
        valueGetter: (item) => getBankName({ code: item.customerBank }),
      },
      {
        header: t('fields.url'),
        valueKey: 'url',
      },
      {
        header: t('fields.device_os'),
        valueGetter: (item) =>
          item.deviceOs ? deviceUtils.getDeviceOsLabel(item.deviceOs) : '-',
      },
    ],
    [t, getBankName],
  );

  return (
    <CrudTable
      columns={columns}
      queryResult={deeplinksQuery}
      actions={[
        {
          type: CrudTableActionType.Details,
          onClick: onEdit,
        },
        {
          type: CrudTableActionType.Remove,
          onRemove: (item: Deeplink, { close }) =>
            removeDeeplinkMutation.mutate(item.id, {
              onSuccess: close,
            }),
        },
      ]}
    />
  );
};
